<script lang="ts">
	import { buildPageSeo } from '$lib/seo';
	import { absoluteUrl, site } from '$lib/site';

	const description =
		'Selected projects by Jarle Mathiesen, including Summit and Matstack.';
	const seo = buildPageSeo({
		title: 'Projects',
		description,
		path: '/projects',
		keywords: ['projects', 'Summit', 'Matstack', 'software engineering', 'Jarle Mathiesen']
	});
</script>

<svelte:head>
	<title>{seo.title}</title>
	<meta name="description" content={seo.description} />
	<meta name="author" content={site.author} />
	<meta name="keywords" content={seo.keywords} />
	<link rel="canonical" href={seo.canonical} />
	<link rel="alternate" type="application/rss+xml" title={`${site.name} RSS`} href={absoluteUrl('/rss.xml')} />
	<meta property="og:type" content={seo.type} />
	<meta property="og:title" content={seo.title} />
	<meta property="og:description" content={seo.description} />
	<meta property="og:url" content={seo.canonical} />
	<meta property="og:image" content={seo.image} />
	<meta property="og:site_name" content={site.name} />
	<meta name="twitter:card" content="summary_large_image" />
	<meta name="twitter:title" content={seo.title} />
	<meta name="twitter:description" content={seo.description} />
	<meta name="twitter:image" content={seo.image} />
</svelte:head>

<article class="page-shell projects-page">
	<header class="page-header">
		<div class="page-header__top">
			<a class="header-link" href="/">Writing</a>
		</div>
		<h1>Projects</h1>
		<p class="intro">A small selection of products and systems I have built.</p>
	</header>

	<section class="project-list" aria-label="Selected projects">
		<article class="project-card">
			<div class="project-card__meta">
				<p class="section-label">Product</p>
			</div>
			<div class="project-card__body">
					<h2><a href="https://summyt.app" rel="noreferrer">summyt.app</a></h2>
				<p>
					A YouTube summarizer with more than 200,000 users, built as both a web app and a
					browser extension.
				</p>
			</div>
		</article>

		<article class="project-card">
			<div class="project-card__meta">
				<p class="section-label">Framework</p>
			</div>
			<div class="project-card__body">
				<h2><a href="https://matstack.dev" rel="noreferrer">matstack.dev</a></h2>
				<p>
					A custom framework for Node.js, presented as a technical framework site rather than a
					general product page.
				</p>
			</div>
		</article>
	</section>

	<footer class="site-footer">
		<a href="/guides">Guides</a>
		<a href="/">Back</a>
		<a href="/rss.xml">RSS</a>
	</footer>
</article>

<style>
	.page-shell {
		width: min(var(--measure-page), calc(100% - var(--space-2xl)));
		margin: 0 auto;
		min-height: 100svh;
		display: flex;
		flex-direction: column;
		padding: calc(var(--space-4xl) + var(--space-sm)) 0 var(--space-5xl);
	}

	.page-header {
		display: grid;
		gap: var(--space-md);
		padding-bottom: calc(var(--space-3xl) + var(--space-sm));
	}

	.header-link {
		font-family: var(--font-mono);
		font-size: 0.82rem;
		letter-spacing: 0.08em;
		text-transform: uppercase;
		color: var(--color-soft);
	}

	.intro {
		max-width: var(--measure-intro-wide);
		font-size: 1.04rem;
		line-height: 1.7;
		color: var(--color-muted);
	}

	.project-list {
		display: grid;
		gap: calc(var(--space-2xl) + var(--space-sm));
	}

	.project-card {
		display: grid;
		grid-template-columns: minmax(0, 9rem) minmax(0, 1fr);
		gap: var(--space-xl);
		align-items: baseline;
	}

	.project-card__meta {
		display: grid;
		align-content: start;
	}

	.section-label {
		font-size: var(--text-label);
		font-family: var(--font-mono);
		font-weight: 600;
		letter-spacing: 0.08em;
		text-transform: uppercase;
		color: var(--color-soft);
	}

	.project-card__body {
		display: grid;
		gap: var(--space-md);
	}

	.project-card__body h2 {
		max-width: var(--measure-post-title);
		font-size: 1.35rem;
		font-family: var(--font-display);
		font-weight: 500;
		line-height: 1.02;
		color: var(--color-ink);
	}

	.project-card__body p {
		max-width: var(--measure-post-copy);
		font-size: 1rem;
		line-height: 1.68;
		color: var(--color-muted);
	}

	.site-footer {
		display: flex;
		gap: var(--space-lg);
		margin-top: auto;
		padding-top: var(--space-3xl);
		font-family: var(--font-mono);
		font-size: 0.82rem;
		letter-spacing: 0.06em;
		text-transform: uppercase;
		color: var(--color-muted);
	}

	@media (max-width: 640px) {
		.page-shell {
			padding-top: var(--space-xl);
			padding-bottom: var(--space-4xl);
		}

		.project-card {
			grid-template-columns: 1fr;
			gap: var(--space-sm);
		}
	}
</style>
